import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import PartnerDataGrid, {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../containers/data-grid";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {PrintLabelsDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../components/base/button/icon_button";
import {Add, Remove} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import Api from "../../../../../../core/services/api/api";
import Button from "../../../../../components/base/button/button";
import BizPrintService from "../../../../../../core/services/biz-print/biz-print-service";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import SelectProductSerialNumbersDialog from "../../../../../components/dialogs/select-product-serial-numbers";
import {deepCopy, deepEqual} from "../../../../../../core/services/utils/utils";
import ToastService from "../../../../../../core/services/toast";
import UploadCsvFileDialog from "../../../../../components/dialogs/upload-csv-file";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {TextField} from "@mui/material";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import PartAutocomplete from "../../../../../components/text-fields/autocomplete/part-autocomplete";

const serializedDataGridSaveConfig = {
    name: "PrintLabelsViewSerializedDataGrid",
    version: 1,
}
const unSerializedDataGridSaveConfig = {
    name: "PrintLabelsViewUnSerializedDataGrid",
    version: 1,
}

const initialSelectSerialsDialogState = {
    open: false,
    productData: null,
    productImages: null,
}

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

const initialUploadCsvFileDialogState = {
    open: false,
    requiredFields: [],
    title: '',
    sampleFileTitle: '',
    onParse: () => void 0,
}

const PrintLabelsView = () => {
    const [serializedProducts, setSerializedProducts] = useState([]);
    const [adding, setAdding] = useState(false);
    const [unSerializedProducts, setUnSerializedProducts] = useState([]);
    const [selectSerialsDialogState, setSelectSerialsDialogState] = useState(initialSelectSerialsDialogState);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const [uploadCsvFileDialogState, setUploadCsvFileDialogState] = useState(initialUploadCsvFileDialogState);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('print-labels-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.printLabels);
        configureBehavioralTool((tags) => tags.printLabels);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Creates the columns for the data grid based on the serialized flag
     * @type {(function(boolean): DataGridColumn[])}
     */
    const createColumns = useCallback((serialized) => {
        return [
            {
                name: PrintLabelsDataGridColumnNames.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                sortable: false,
                width: 0,
            },
            {
                title: "Part No",
                name: PrintLabelsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            ...(serialized
                ? [{
                    title: "Serial No",
                    name: PrintLabelsDataGridColumnNames.serialNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                }]
                : []),
            {
                title: "Repeat Count",
                name: PrintLabelsDataGridColumnNames.repeatCount,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 140,
                sortable: false,
            },
        ]
    }, []);

    const serializedColumns = useMemo(() => createColumns(true), [createColumns]);
    const unSerializedColumns = useMemo(() => createColumns(false), [createColumns]);

    const [serializedDataGridColumns, , , serializedDataGridDensity] = useDataGridSavedState({
        columns: serializedColumns,
        name: serializedDataGridSaveConfig.name,
        version: serializedDataGridSaveConfig.version,
    })
    const [unSerializedDataGridColumns, , , unSerializedDataGridDensity] = useDataGridSavedState({
        columns: unSerializedColumns,
        name: unSerializedDataGridSaveConfig.name,
        version: unSerializedDataGridSaveConfig.version,
    })

    /**
     * Creates the data for the data grid row.
     * @type {function(*, *, null=): {printInfo: {image, cost: *, mainCategory: *, upc: *, avgCost: *, partNo}, id: *, partNo, serialNo: null, repeatCount: number}}
     */
    const createProductData = useCallback((productInfo, productImages, serialNo = null) => ({
        partNo: productInfo.partNo ?? productInfo.partNumber,
        serialNo: serialNo,
        id: productInfo.id,
        repeatCount: 1,
        printInfo: {
            partNo: productInfo?.partNo ?? productInfo.partNumber,
            upc: productInfo?.upc,
            cost: productInfo?.cost,
            avgCost: productInfo?.avgCost,
            mainCategory: productInfo?.category?.title,
            serialNo: serialNo,
            image: (productImages?.find(e => e.isCover) ?? (productImages?.length ? productImages[0] : undefined))?.url,
            description: productInfo?.description,
        },
    }), [])

    /**
     * Adds the product to the list of products based on the serialized flag
     *
     * - If the product is serialized, the user has to pick which serials they want to add
     * else the product is added to the list of products
     * @type {(function(*): Promise<void>)|*}
     */
    const onProductSelected = useCallback(async (product) => {
        setAdding(true);
        const [productResponse, productImagesResponse] = await Promise.all([
            Api.getOperatorPartFullInfo(product.id),
            Api.getOperatorPartImages(product.id),
        ]);
        if (!isMounted())
            return;
        setAdding(false);
        if (!productResponse?.resultFlag || !productImagesResponse.resultFlag) {
            return;
        }
        const productData = productResponse.data;
        const productImages = productImagesResponse.data;
        if (productData.isSerializable) {
            // user has to pick which serials they want to add
            setSelectSerialsDialogState({open: true, productData, productImages});
        } else {
            setUnSerializedProducts((prev) => [...prev, createProductData(productData, productImages)]);
        }
    }, [createProductData, isMounted]);

    /**
     * Adds the selected serials to the list of products based on the serialized flag
     * @type {(function(*, *, *): void)}
     */
    const onSerialsSelected = useCallback((_serialNumbers, productData, productImages) => {
        setSerializedProducts((prev) => {
            const serialNumbers = deepCopy(_serialNumbers);
            const res = [];
            for (const prevElement of prev) {
                const index = serialNumbers.findIndex((e) => e === prevElement.serialNo);
                const exists = index !== -1;
                if (exists) {
                    serialNumbers.splice(index, 1);
                }
                res.push({
                    ...prevElement,
                    repeatCount: prevElement.repeatCount + (exists ? 1 : 0),
                });
            }
            for (const serialNumber of serialNumbers) {
                res.push(createProductData(productData, productImages, serialNumber));
            }
            return res;
        });
    }, [createProductData]);

    /**
     * Removes item from the list of products based on the serialized flag and the item properties
     * @type {(function(*, *): void)}
     */
    const removeItem = useCallback((item, serialized) => {
        if (serialized) {
            setSerializedProducts((prev) => prev.filter((i) => !deepEqual(i, item)))
        } else {
            setUnSerializedProducts((prev) => prev.filter((i) => !deepEqual(i, item)))
        }
    }, []);

    /**
     * Changes the repeat count of the item based on the serialized flag and the change value.
     * @type {(function(*, *, *): void)}
     */
    const changeRepeatCount = useCallback((item, serialized, change) => {
        if (serialized) {
            setSerializedProducts((prev) => prev.map((i) => i.partNo === item.partNo && i.serialNo === item.serialNo ? ({
                ...i,
                repeatCount: Math.max((i.repeatCount ?? 0) + change, 0)
            }) : i))
        } else {
            setUnSerializedProducts((prev) => prev.map((i) => i.partNo === item.partNo ? ({
                ...i,
                repeatCount: Math.max((i.repeatCount ?? 0) + change, 0)
            }) : i))
        }
    }, []);

    /**
     * Changes the repeat count of the item based on the serialized flag and the value.
     * @type {(function(*, *, *): void)}
     */
    const onRepeatCountChanged = useCallback((serialized, item, value) => {
        if (serialized) {
            setSerializedProducts((prev) => prev.map((i) => i.partNo === item.partNo && i.serialNo === item.serialNo ? ({
                ...i,
                repeatCount: value
            }) : i))
        } else {
            setUnSerializedProducts((prev) => prev.map((i) => i.partNo === item.partNo ? ({
                ...i,
                repeatCount: value
            }) : i))
        }
    }, []);

    /**
     * Changes the repeat count of the item based on the serialized flag and the value. If the value is not a number, it will be set to 0.
     * @type {(function(*, *, *): void)}
     */
    const onRepeatCountBlurred = useCallback((serialized, item, value) => {
        let num = Number(value);

        if (Number.isNaN(num) || num < 0)
            num = 0;

        num = parseInt(num.toFixed().toString());

        if (serialized) {
            setSerializedProducts((prev) => prev.map((i) =>
                i.partNo === item.partNo && i.serialNo === item.serialNo
                    ? ({...i, repeatCount: num})
                    : i
            ))
        } else {
            setUnSerializedProducts((prev) => prev.map((i) =>
                i.partNo === item.partNo
                    ? ({...i, repeatCount: num})
                    : i
            ))
        }
    }, []);

    /**
     * Parses the csv file and adds the products to the list of products based on the serialized flag.
     * @type {(function(*, *): Promise<undefined|number|string>)|*}
     */
    const onCsvParsed = useCallback(async (parsed, serialized) => {
        let entries = parsed.map(({partNo, serialNo, repeatCount}) => ({
            partNo: partNo?.trim(),
            serialNo: serialized ? serialNo?.trim() : null,
            repeatCount: parseInt(repeatCount?.trim()),
        }))

        if (!entries)
            return;

        const getUniqueEntries = (entries, fetcher) => [...new Set(entries.map((e) => fetcher(e)))];

        setAdding(true);
        const validationResponse = await Api.validatePartNumbers({
            partNumbers: getUniqueEntries(entries, e => e.partNo),
        })
        if (!isMounted())
            return;

        setAdding(false);

        if (!validationResponse?.resultFlag)
            return ToastService.error({
                title: "Error",
                message: "Error validating part numbers",
            })

        let validEntries = entries.filter((e) => !!validationResponse.data[e.partNo]);
        if (!validEntries.length)
            return ToastService.warning({
                title: "Warning",
                message: "No valid part numbers found",
            });

        setAdding(true);

        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: getUniqueEntries(validEntries, e => e.partNo),
        });

        if (!isMounted())
            return;

        setAdding(false);

        if (!partInfoResponse?.resultFlag)
            return ToastService.error({
                title: "Error",
                message: "Error getting part info",
            })

        const partInfoResponseData = Object.fromEntries(partInfoResponse.data.map(e => [e.partNo, e,]));

        validEntries = validEntries.map((e) => ({
            ...e,
            id: partInfoResponseData[e.partNo].id,
        }));

        setAdding(true);
        // fetch full info of each product.
        for (const id of getUniqueEntries(validEntries, e => e.id)) {
            const [productResponse, productImagesResponse] = await Promise.all([
                Api.getOperatorPartFullInfo(id),
                Api.getOperatorPartImages(id),
            ]);
            if (!isMounted())
                return;
            if (!productResponse?.resultFlag || !productImagesResponse.resultFlag) {
                continue;
            }
            const productData = productResponse.data;
            const productImages = productImagesResponse.data;
            // create the print info for the product
            validEntries.forEach((entry) => {
                if (entry.id === id) {
                    entry.printInfo = createProductData(productData, productImages).printInfo;
                    entry.printInfo.serialNo = entry.serialNo;
                }
            });
        }
        setAdding(false);

        if (serialized) {
            setSerializedProducts((prev) => {
                const res = deepCopy(prev);
                for (const entry of validEntries) {
                    const index = res.findIndex((e) => e.partNo === entry.partNo && e.serialNo === entry.serialNo);
                    if (index === -1) {
                        res.push(entry);
                    } else {
                        res[index].repeatCount += entry.repeatCount;
                    }
                }
                return res;
            });
        } else {
            setUnSerializedProducts((prev) => {
                const res = deepCopy(prev);
                for (const entry of validEntries) {
                    const index = res.findIndex((e) => e.partNo === entry.partNo);
                    if (index === -1) {
                        res.push(entry);
                    } else {
                        res[index].repeatCount += entry.repeatCount;
                    }
                }
                return res;
            });
        }
    }, [createProductData, isMounted])

    /**
     * Imports the products from a csv file based on the serialized flag.
     * @type {(function(*): Promise<void>)}
     */
    const importFromCsv = useCallback(async (serialized) => {
        setUploadCsvFileDialogState({
            open: true,
            title: `Import ${serialized ? "Serialized" : "Un-Serialized"} product labels From Csv`,
            sampleFileTitle: `import-${serialized ? "serialized" : "unserialized"}-product-labels-sample-file`,
            requiredFields: [
                {
                    name: "partNo",
                    title: "Part No",
                    generator: (i) => `partNumber${i}`,
                },
                ...(serialized
                    ? [{
                        name: "serialNo",
                        title: "Serial No",
                        generator: (i) => `serialNumber${i}`,
                    }]
                    : []),
                {
                    name: "repeatCount",
                    title: "Repeat Count",
                    generator: (i) => i,
                },
            ],
            onParse: (parsed) => onCsvParsed(parsed, serialized),
        });
    }, [onCsvParsed]);


    /**
     * Creates the rows for the data grid based on the serialized flag and the list of products.
     * @type {function(boolean): DataGridRow[]}
     */
    const createRows = useCallback((serialized) => {
        const data = serialized ? serializedProducts : unSerializedProducts;
        return data.map((item) => ({
            key: `${item.id}-${item.partNo}-${item.serialNo ?? ''}`,
            data: item,
            cells: {
                [PrintLabelsDataGridColumnNames.remove]:
                    <IconButton
                        onPressed={() => removeItem(item, serialized)}
                        color={'paper.error'}
                    >
                        <RemoveIcon className={'icon small'}/>
                    </IconButton>,
                [PrintLabelsDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={item?.partNo}>
                        {item?.partNo ?? ''}
                    </ProductInformationPopover>,
                [PrintLabelsDataGridColumnNames.serialNo]: item.serialNo,
                [PrintLabelsDataGridColumnNames.repeatCount]:
                    <div className={'d-flex align-items-center justify-content-center'}>
                        <IconButton
                            onPressed={() => changeRepeatCount(item, serialized, -1)}
                            color={'secondary'}
                        >
                            <Remove className={'icon small'}/>
                        </IconButton>
                        <div className={'quantity-field mx-2'}>
                            <TextField
                                size={'small'}
                                fullWidth
                                inputProps={{min: 0, max: 999}}
                                type={'number'}
                                placeholder={''}
                                sx={{
                                    '& input': {
                                        textAlign: 'center',
                                    }
                                }}
                                value={item.repeatCount?.toString() ?? ''}
                                disabled={adding}
                                onBlur={(e) => onRepeatCountBlurred(serialized, item, e.target.value)}
                                onChange={(value) => onRepeatCountChanged(serialized, item, value)}
                            />
                        </div>
                        <IconButton
                            onPressed={() => changeRepeatCount(item, serialized, 1)}
                            color={'secondary'}
                        >
                            <Add className={'icon small'}/>
                        </IconButton>
                    </div>,
            },
        }))
    }, [changeRepeatCount, onRepeatCountBlurred, onRepeatCountChanged, adding, removeItem, serializedProducts, unSerializedProducts])

    const serializedDataGridRows = useMemo(() => createRows(true), [createRows]);
    const unSerializedDataGridRows = useMemo(() => createRows(false), [createRows]);

    /**
     * Opens the print dialog with the serialized or un-serialized page based on the serialized flag.
     * @type {(function(*): void)}
     */
    const printLabels = useCallback((serialized) => {
        setBizPrintDialogState({
            open: true,
            pageName: serialized
                ? BizPrintService.pages.productLabelsSerialized.name
                : BizPrintService.pages.productLabelsUnSerialized.name,
            pageData: (serialized ? serializedProducts : unSerializedProducts)
                .reduce((acc, curr) => {
                    for (let i = 0; i < curr.repeatCount; i++)
                        acc.push(curr);
                    return acc;
                }, [])
                .map(e => e.printInfo),
        })
    }, [serializedProducts, unSerializedProducts])

    /**
     * Filters the options for the part auto complete based on the serialized and un-serialized products.
     * @type {function(*): *}
     */
    const filterPartAutocompleteOptions = useCallback((options) => {
        return options.filter(o => !serializedProducts.find(p => p.partNo === o.partNo) && !unSerializedProducts.find(p => p.partNo === o.partNo));
    }, [serializedProducts, unSerializedProducts])

    return (
        <>
            <div className={'print-labels-view'}>
                <div className={'ml-2 my-4'}>
                    <p className={'view-title'}>
                        Print Labels
                    </p>
                </div>
                <Row>
                    <Col xs={12} className={'mb-5'}>
                        <PartAutocomplete
                            disableCloseOnSelect
                            value={null}
                            label={"Products"}
                            disabled={adding}
                            onChange={(_, value) => onProductSelected(value)}
                            filterOptions={filterPartAutocompleteOptions}
                            textFieldProps={{
                                variant: 'filled',
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Col>
                    <Col xs={12} lg={6} className={'mb-4'}>
                        <Paper className={'paper d-flex flex-column h-100'}>
                            <div className={'section-header'}>
                                <div className={'d-flex align-items-center'}>
                                    <p>
                                        Serialized Products
                                    </p>
                                    <p/>
                                </div>
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        className={'mr-2'}
                                        onClick={() => importFromCsv(true)}
                                        disabled={adding}
                                    >
                                        {
                                            adding
                                                ? "Loading..."
                                                : "Import From Csv"
                                        }
                                    </Button>
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={() => printLabels(true)}
                                        disabled={adding || !serializedDataGridRows.length}
                                    >
                                        {
                                            adding
                                                ? "Loading..."
                                                : "Print"
                                        }
                                    </Button>
                                </div>
                            </div>
                            <PartnerDataGrid
                                {...serializedDataGridSaveConfig}
                                initialLayoutState={{columns: serializedColumns}}
                                rows={serializedDataGridRows}
                                columns={serializedDataGridColumns}
                                hideExporting
                                hideFooter
                                visibleRows={10}
                                state={{
                                    loading: {state: adding},
                                    density: serializedDataGridDensity,
                                }}
                            />
                        </Paper>
                    </Col>
                    <Col xs={12} lg={6} className={'mb-4'}>
                        <Paper className={'paper d-flex flex-column h-100'}>
                            <div className={'section-header'}>
                                <div className={'d-flex align-items-center'}>
                                    <p>
                                        Un-serialized Products
                                    </p>
                                    <p/>
                                </div>
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        className={'mr-2'}
                                        onClick={() => importFromCsv(false)}
                                        disabled={adding}
                                    >
                                        {
                                            adding
                                                ? "Loading..."
                                                : "Import From Csv"
                                        }
                                    </Button>
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={() => printLabels(false)}
                                        disabled={adding || !unSerializedDataGridRows.length}
                                    >
                                        {
                                            adding
                                                ? "Loading..."
                                                : "Print"
                                        }
                                    </Button>
                                </div>
                            </div>
                            <PartnerDataGrid
                                {...unSerializedDataGridSaveConfig}
                                initialLayoutState={{columns: unSerializedColumns}}
                                rows={unSerializedDataGridRows}
                                columns={unSerializedDataGridColumns}
                                hideExporting
                                hideFooter
                                visibleRows={10}
                                state={{
                                    loading: {state: adding},
                                    density: unSerializedDataGridDensity,
                                }}
                            />
                        </Paper>
                    </Col>
                </Row>
            </div>
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
            <SelectProductSerialNumbersDialog
                freeSolo
                open={selectSerialsDialogState.open}
                close={() => setSelectSerialsDialogState(initialSelectSerialsDialogState)}
                partNo={selectSerialsDialogState.productData?.partNo ?? selectSerialsDialogState.productData?.partNumber}
                onFinish={(serials) => onSerialsSelected(serials, selectSerialsDialogState.productData, selectSerialsDialogState.productImages)}
            />
            <UploadCsvFileDialog
                open={uploadCsvFileDialogState.open}
                close={() => setUploadCsvFileDialogState(initialUploadCsvFileDialogState)}
                title={uploadCsvFileDialogState.title}
                sampleFileTitle={uploadCsvFileDialogState.sampleFileTitle}
                requiredFields={uploadCsvFileDialogState.requiredFields}
                onParse={uploadCsvFileDialogState.onParse}
            />
        </>
    )
}

export default PrintLabelsView;
